import { Box, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import React from "react";

// Styled hero title with gradient and responsive design
export const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  background: "linear-gradient(135deg, #FF4C00 0%, #FF8A00 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",

  // Default styles (mobile first)
  fontSize: "2rem",
  lineHeight: 1.2,
  letterSpacing: "-0.5px",

  // Responsive styles using breakpoints
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.5rem",
    lineHeight: 1.3,
    letterSpacing: "-1px",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "3rem",
    lineHeight: 1.4,
    letterSpacing: "-1.5px",
  },
  [theme.breakpoints.up("lg")]: {
    fontSize: "3.5rem",
  },
}));

// Styled hero subtitle with responsive design
export const HeroSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[300],
  marginBottom: theme.spacing(4),

  // Default styles (mobile first)
  fontSize: "1rem",
  lineHeight: 1.5,
  maxWidth: "100%",
  opacity: 0.9,

  // Responsive styles using breakpoints
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.15rem",
    lineHeight: 1.6,
    maxWidth: "80%",
    opacity: 1,
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.25rem",
    lineHeight: 1.7,
    maxWidth: "600px",
  },
}));

const StyledHeroSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRtl",
})(({ theme, isRtl }) => ({
  position: "relative",
  minHeight: "60vh",
  display: "flex",
  alignItems: "center",
  color: "#fff",
  overflow: "hidden",
  isolation: "isolate", // Creates a new stacking context

  // Primary background with multiple gradients for depth
  background: `
    linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(45, 45, 45, 0.95) 100%),
    radial-gradient(circle at 50% 50%, #2D2D2D 0%, #1E1E1E 100%)
  `,

  // Animated gradient mesh background
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `
      linear-gradient(90deg, transparent 0%, rgba(255, 76, 0, 0.03) 25%, transparent 50%),
      linear-gradient(-90deg, transparent 0%, rgba(255, 138, 0, 0.03) 75%, transparent 100%)
    `,
    backgroundSize: "200% 200%",
    animation: "gradient 15s ease infinite",
    zIndex: 1,
    opacity: 0.7,
  },

  // Decorative accent lines
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    right: isRtl ? 'auto' : 0,
    left: isRtl ? 0 : 'auto',
    width: "45%",
    height: "2px",
    background: "linear-gradient(135deg, #FF4C00 0%, #FF8A00 100%)",
    boxShadow: "0 0 20px rgba(255, 76, 0, 0.3)",
    zIndex: 2,
  },

  // Left accent line
  "& > div::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: isRtl ? 'auto' : 0,
    right: isRtl ? 0 : 'auto',
    width: "25%",
    height: "2px",
    background: "linear-gradient(135deg, #FF8A00 0%, #FF4C00 100%)",
    boxShadow: "0 0 20px rgba(255, 138, 0, 0.3)",
    zIndex: 2,
  },

  // Content positioning
  "& > *": {
    position: "relative",
    zIndex: 3,
  },

  // Mesh grid overlay
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: "50px 50px",
    mask: "linear-gradient(black, transparent)",
    zIndex: 2,
  },

  // Responsive adjustments
  [theme.breakpoints.down("md")]: {
    minHeight: "50vh",
    "&::after": {
      width: "35%",
    },
    "& > div::before": {
      width: "20%",
    },
  },

  [theme.breakpoints.down("sm")]: {
    minHeight: "40vh",
    "&::after": {
      width: "25%",
    },
    "& > div::before": {
      width: "15%",
    },
  },

  // Hover effects
  "&:hover": {
    "&::before": {
      opacity: 0.9,
      transition: "opacity 0.5s ease",
    },
    "& > div::before, &::after": {
      width: "50%",
      transition: "width 0.5s ease",
    },
  },

  // Animation keyframes
  "@keyframes gradient": {
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  },
}));

export const HeroSection = ({ children }) => {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  
  return <StyledHeroSection isRtl={isRtl}>{children}</StyledHeroSection>;
};
